import Tabs from "@/components/Tabs";
import { Path, useNavigate } from "@/router";
import { useLocation, useParams } from "react-router-dom";
import { EventWrapper } from "./[eventSlug]/types";
import { useCanViewRecruitingEventRecapsBool } from "@/hooks/useCapabilities";
import { useEventsBasePath } from "./_eventsBasePathContext";
import { getEventTypeName } from "./[eventSlug]/utils";

const tabs = [
  "overview",
  "registrations",
  "waitlist",
  "cancellations",
  "attendance",
  "results",
  "payouts",
  "notes"
] as const;
const recruitmentTabs = ["overview", "leads", "recap", "notes"] as const;
type TabType = (typeof tabs)[number];
type RecruitmentTabType = (typeof recruitmentTabs)[number];

interface EventTabsProps {
  eventData: EventWrapper["event"] | undefined;
  isPatFinalized?: boolean;
  isPayoutSubmitted?: boolean;
  isStatusPending?: boolean;
  hasLateCancellation?: boolean;
}

export default function EventTabs({
  eventData,
  isPatFinalized = true,
  isPayoutSubmitted = true,
  isStatusPending = false,
  hasLateCancellation = false
}: EventTabsProps) {
  const { eventSlug } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const canExportRecruitingEventDetails = useCanViewRecruitingEventRecapsBool();
  const basePath = useEventsBasePath();

  if (!eventSlug) return null;

  const isPAT = getEventTypeName(eventData) === "PAT";
  const isRecruitment = getEventTypeName(eventData) === "Recruitment";
  const isRecruitingFinalized = !!eventData?.recruitmentEventAttributes?.isFinalized;
  const shouldShowResultsBadge = isPAT && isStatusPending && !isPatFinalized;
  const shouldShowPayoutsBadge = isPAT && isStatusPending && !isPayoutSubmitted;
  const shouldShowLateCancellationBadge = isPAT && !isPatFinalized && hasLateCancellation;

  // For Recruitment events, show different tabs
  if (isRecruitment) {
    const recruitmentTabOptions: Record<RecruitmentTabType, { label: string; path: string; isAvailable: boolean }> = {
      overview: {
        label: "Overview",
        path: `${basePath}/${eventSlug}/overview`,
        isAvailable: true
      },
      leads: {
        label: "Leads",
        path: `${basePath}/${eventSlug}/leads`,
        isAvailable: true
      },
      // Recap tab should be available if the recruiting event is not finalized
      // or the user has the ability to export recruiting event details
      recap: {
        label: "Recap",
        path: `${basePath}/${eventSlug}/recap`,
        isAvailable: !isRecruitingFinalized || canExportRecruitingEventDetails
      },
      notes: {
        label: "Notes and Tasks",
        path: `${basePath}/${eventSlug}/notes`,
        isAvailable: true
      }
    };

    const recruitmentTabItems = recruitmentTabs
      .filter((tab) => recruitmentTabOptions[tab].isAvailable)
      .map((tab) => ({
        label: recruitmentTabOptions[tab].label,
        active: currentUrl.startsWith(recruitmentTabOptions[tab].path),
        onClick: () => navigate(recruitmentTabOptions[tab].path as Path)
      }));

    return (
      <div>
        <Tabs tabItems={recruitmentTabItems} />
      </div>
    );
  }

  const tabOptions: Record<TabType, { label: string; path: string; isAvailable: boolean }> = {
    overview: {
      label: "Overview",
      path: `${basePath}/${eventSlug}/overview`,
      isAvailable: true
    },
    registrations: {
      label: "Registrations",
      path: `${basePath}/${eventSlug}/registrations`,
      isAvailable: true
    },
    waitlist: {
      label: "Waitlist",
      path: `${basePath}/${eventSlug}/waitlist`,
      isAvailable: !!eventData?.hasWaitlist
    },
    cancellations: {
      label: "Cancellations",
      path: `${basePath}/${eventSlug}/cancellations`,
      isAvailable: true
    },
    attendance: {
      label: "Attendance",
      path: `${basePath}/${eventSlug}/attendance`,
      isAvailable: !!eventData?.collectsAttendance && !isPAT
    },
    results: {
      label: "Results",
      path: `${basePath}/${eventSlug}/results`,
      isAvailable: isPAT
    },
    payouts: {
      label: "Payouts",
      path: `${basePath}/${eventSlug}/payouts`,
      isAvailable: isPAT
    },
    notes: {
      label: "Notes and Tasks",
      path: `${basePath}/${eventSlug}/notes`,
      isAvailable: true
    }
  };

  const tabItems = tabs
    .filter((tab) => tabOptions[tab].isAvailable)
    .map((tab) => ({
      label: tabOptions[tab].label,
      active: currentUrl.startsWith(tabOptions[tab].path),
      onClick: () => navigate(tabOptions[tab].path as Path),
      ...(tab === "results" && shouldShowResultsBadge && { badgeColor: "error" as const }),
      ...(tab === "payouts" && shouldShowPayoutsBadge && { badgeColor: "error" as const }),
      ...(tab === "registrations" && shouldShowLateCancellationBadge && { badgeColor: "error" as const })
    }));

  return (
    <div>
      <Tabs tabItems={tabItems} />
    </div>
  );
}
